import {
  useQuery,
  useQueryClient,
  useMutation,
  keepPreviousData,
  useQueries,
} from '@tanstack/vue-query'
import type {
  ResponseByPath,
  SchemaAttributes,
  QueryParamsByPath,
  BodyByPath,
} from '@@/api/types'
import { useApi } from '@/composables/useApi.js'
import {
  buildIncludedResourceMap,
  createRelationshipBuilder,
  one,
} from '@/utils/jsonapi.js'

const REMINDER_TEMPLATES_URL = '/{provet_id}/api/reminder_templates'
const REMINDER_TEMPLATE_BY_ID_URL = '/{provet_id}/api/reminder_templates/{id}'

export type ReminderTemplateAttributes = SchemaAttributes<'ReminderTemplate'>

export type ReminderTemplateResponse = ResponseByPath<
  'get',
  typeof REMINDER_TEMPLATES_URL
>

export type ReminderTemplateByIdResponse = ResponseByPath<
  'get',
  typeof REMINDER_TEMPLATE_BY_ID_URL
>

export type ReminderTemplateCreateBody = BodyByPath<
  'post',
  typeof REMINDER_TEMPLATES_URL
>

export type ReminderTemplateUpdateBody = BodyByPath<
  'patch',
  typeof REMINDER_TEMPLATE_BY_ID_URL
>

export type ReminderTemplateQueryParams = QueryParamsByPath<
  'get',
  typeof REMINDER_TEMPLATES_URL
>

type CtaConfig = {
  enabled: boolean
  text: string
  action: string | null
}

type ReminderTemplateCore = {
  title: string
  emailSubject?: string
  healthServiceName?: string
  emailText: string
  smsText: string
  postText: string
  primary: CtaConfig
  secondary: CtaConfig
  banner?: string | null
  updateExisting?: boolean
}

export interface ReminderTemplate extends ReminderTemplateCore {
  id: string
  created?: string
  modified?: string
  department?: {
    id: string
  }
}

export interface ReminderTemplateMutationData {
  title: string
  emailSubject?: string | null
  healthServiceName?: string | null
  emailText?: string
  smsText?: string
  postText?: string
  primary?: Partial<CtaConfig>
  secondary?: Partial<CtaConfig>
  updateExisting?: boolean
  departmentId?: string
}

export type CreateReminderTemplateData = Omit<
  ReminderTemplateMutationData,
  'updateExisting'
>

export type UpdateReminderTemplateData = ReminderTemplateMutationData

const reminderTemplateRelationships = {
  department: one('Department'),
}

export const buildReminderTemplateRelationships = createRelationshipBuilder(
  reminderTemplateRelationships,
)
const buildReminderTemplateRequest = (
  data: ReminderTemplateMutationData,
): {
  attributes: ReminderTemplateCreateBody['data']['attributes']
  relationships: ReminderTemplateCreateBody['data']['relationships']
} => {
  return {
    attributes: {
      title: data.title,
      email_subject: data.emailSubject || undefined,
      health_service_name: data.healthServiceName || undefined,
      email_text: data.emailText || null,
      sms_text: data.smsText || null,
      post_text: data.postText || null,
      primary_cta_enabled: data.primary?.enabled || false,
      primary_cta_text: data.primary?.text || '',
      primary_cta_action: data.primary?.action || null,
      secondary_cta_enabled: data.secondary?.enabled || false,
      secondary_cta_text: data.secondary?.text || '',
      secondary_cta_action: data.secondary?.action || null,
      update_existing: data.updateExisting || false,
    },
    relationships: {
      department: {
        data: data.departmentId
          ? {
              id: data.departmentId,
              type: 'Department',
            }
          : null,
      },
    },
  }
}

type ReminderTemplateResource =
  | ReminderTemplateByIdResponse['data']
  | ReminderTemplateResponse['data'][number]

const mapReminderTemplate = (
  template: ReminderTemplateResource,
  included: unknown,
): ReminderTemplate => {
  const relationships = buildReminderTemplateRelationships(
    template,
    included as unknown as ReturnType<typeof buildIncludedResourceMap>,
  )

  const attributes = template.attributes

  return {
    id: template.id,
    title: attributes?.title || '',
    emailSubject: attributes?.email_subject || undefined,
    healthServiceName: attributes?.health_service_name || undefined,
    emailText: attributes?.email_text || '',
    smsText: attributes?.sms_text || '',
    postText: attributes?.post_text || '',
    created: attributes?.created,
    modified: attributes?.modified,
    primary: {
      enabled: attributes?.primary_cta_enabled || false,
      text: attributes?.primary_cta_text || '',
      action: attributes?.primary_cta_action || null,
    },
    secondary: {
      enabled: attributes?.secondary_cta_enabled || false,
      text: attributes?.secondary_cta_text || '',
      action: attributes?.secondary_cta_action || null,
    },
    banner: attributes?.banner || null,
    updateExisting: attributes?.update_existing || false,
    department: relationships.department
      ? {
          id: relationships.department.id,
        }
      : undefined,
  }
}

const reminderTemplateKeys = {
  all: ['reminders-templates'] as const,
  list: (provetId: string, queryParams: ReminderTemplateQueryParams) =>
    [...reminderTemplateKeys.all, provetId, queryParams] as const,
  detail: (provetId: string, templateId: string) =>
    ['reminder-template', provetId, templateId] as const,
}

const normalizeData = (
  response: ReminderTemplateResponse,
): ReminderTemplate[] => {
  const included = buildIncludedResourceMap(response || {}, ['Department'])

  return response.data.map((template) =>
    mapReminderTemplate(template, included),
  )
}

const normalizeSingleData = (
  response: ReminderTemplateByIdResponse,
): ReminderTemplate & { meta?: { available_placeholders?: string[] } } => {
  const included = buildIncludedResourceMap(response || {}, ['Department'])
  const normalized = mapReminderTemplate(response.data, included)

  return {
    ...normalized,
    meta: response.meta,
  }
}

// ? reminder template GET to get all of them
export function useRemindersTemplatesQuery({
  includes = [],
  enabled = false, // to avoid fetching if not on the relevant page, right? 🤔
}: {
  includes?: MaybeRef<string[]>
  enabled?: MaybeRef<boolean>
}) {
  const api = useApi()
  const provetId = useRouteParams<string>('provetId')

  const queryParams = computed<ReminderTemplateQueryParams>(() => ({
    include: unref(includes),
  }))

  const query = useQuery({
    queryKey: computed(() =>
      reminderTemplateKeys.list(provetId.value, queryParams.value),
    ),
    queryFn: () =>
      api((client) =>
        client.GET(REMINDER_TEMPLATES_URL, {
          params: {
            path: {
              provet_id: provetId.value,
            },
            query: queryParams.value,
          },
        }),
      ),
    enabled: unref(enabled),
    select: (response) => ({
      ...response,
      data: normalizeData(response),
    }),
  })

  return reactive(query)
}

// ? reminder template GET with search support for dropdowns
export function useReminderTemplatesListQuery({
  search = '',
  departmentId,
  enabled = true,
}: {
  search?: MaybeRef<string>
  departmentId?: MaybeRef<string | null | undefined>
  enabled?: MaybeRef<boolean>
} = {}) {
  const api = useApi()
  const provetId = useRouteParams<string>('provetId')

  const queryParams = computed<ReminderTemplateQueryParams>(() => {
    const searchValue = unref(search)
    const deptId = unref(departmentId)
    return {
      ...(searchValue ? { 'filter[search]': searchValue } : {}),
      ...(deptId ? { 'filter[available_for_department]': Number(deptId) } : {}),
    }
  })

  const query = useQuery({
    placeholderData: keepPreviousData,
    queryKey: computed(() =>
      reminderTemplateKeys.list(provetId.value, queryParams.value),
    ),
    queryFn: () =>
      api((client) =>
        client.GET(REMINDER_TEMPLATES_URL, {
          params: {
            path: {
              provet_id: provetId.value,
            },
            query: queryParams.value,
          },
        }),
      ),
    enabled,
    select: (response) =>
      response.data.map((template) => ({
        value: template.id,
        label: template.attributes?.title ?? '',
        hasEmail: Boolean(template.attributes?.email_text),
        hasSms: Boolean(template.attributes?.sms_text),
        hasPost: Boolean(template.attributes?.post_text),
      })),
  })

  return reactive(query)
}

// ? reminder template GET to get the specs of a specific one
export function useReminderTemplateByIdQuery({
  reminderTemplateId,
  includes = [],
  enabled = false,
}: {
  reminderTemplateId: MaybeRef<string>
  includes?: MaybeRef<string[]>
  enabled?: MaybeRef<boolean>
}) {
  const api = useApi()
  const provetId = useRouteParams<string>('provetId')

  const templateId = unref(reminderTemplateId)
  const queryParams = computed<ReminderTemplateQueryParams>(() => ({
    include: unref(includes),
  }))

  const query = useQuery({
    queryKey: computed(() =>
      reminderTemplateKeys.detail(provetId.value, templateId),
    ),
    queryFn: () =>
      api((client) =>
        client.GET(REMINDER_TEMPLATE_BY_ID_URL, {
          params: {
            path: {
              provet_id: provetId.value,
              id: templateId,
            },
            query: queryParams.value,
          },
        }),
      ),
    enabled: unref(enabled),
    select: (response) => ({
      ...response,
      data: normalizeSingleData(response),
    }),
  })

  return reactive(query)
}

// ? fetch a set of reminder templates by their IDs (for merging missing selected options)
export function useReminderTemplatesByIdsQuery(ids: MaybeRef<string[]>) {
  const api = useApi()
  const provetId = useRouteParams<string>('provetId')

  return useQueries({
    queries: computed(() =>
      (unref(ids) ?? []).filter(Boolean).map((id) => ({
        queryKey: reminderTemplateKeys.detail(provetId.value, id),
        queryFn: () =>
          api((client) =>
            client.GET(REMINDER_TEMPLATE_BY_ID_URL, {
              params: {
                path: {
                  provet_id: provetId.value,
                  id,
                },
              },
            }),
          ),
        select: (response: ReminderTemplateByIdResponse) => ({
          value: response.data.id,
          label: response.data.attributes?.title ?? '',
          hasEmail: Boolean(response.data.attributes?.email_text),
          hasSms: Boolean(response.data.attributes?.sms_text),
          hasPost: Boolean(response.data.attributes?.post_text),
        }),
      })),
    ),
  })
}

// ? reminder template POST to create a new reminder template
export function useReminderTemplateCreateMutation() {
  const api = useApi()
  const route = useRoute()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (data: CreateReminderTemplateData) => {
      const built = buildReminderTemplateRequest(data)
      const requestBody: ReminderTemplateCreateBody = {
        data: {
          type: 'ReminderTemplate',
          attributes: built.attributes,
          relationships: built.relationships,
        },
      }

      return api((client) =>
        client.POST(REMINDER_TEMPLATES_URL, {
          params: {
            path: { provet_id: route.params.provetId },
          },
          body: requestBody,
        }),
      )
    },
    onSuccess: (response) => {
      const newItem = response.data as ReminderTemplateResponse['data'][number]
      queryClient.setQueriesData<ReminderTemplateResponse>(
        { queryKey: reminderTemplateKeys.all },
        (old) => {
          if (!old?.data) {
            return old
          }
          return { ...old, data: [...old.data, newItem] }
        },
      )
    },
  })
}

// ? reminder template PATCH to update a new reminder template
export function useReminderTemplateUpdateMutation(reminderTemplateId: string) {
  const api = useApi()
  const route = useRoute()
  const queryClient = useQueryClient()
  const provetId = useRouteParams<string>('provetId')

  return useMutation({
    mutationFn: (data: UpdateReminderTemplateData) => {
      const built = buildReminderTemplateRequest(data)
      const requestBody: ReminderTemplateUpdateBody = {
        data: {
          type: 'ReminderTemplate',
          id: reminderTemplateId,
          attributes: built.attributes,
          relationships: built.relationships,
        },
      }

      return api((client) =>
        client.PATCH(REMINDER_TEMPLATE_BY_ID_URL, {
          params: {
            path: { provet_id: route.params.provetId, id: reminderTemplateId },
          },
          body: requestBody,
        }),
      )
    },
    onSuccess: (response) => {
      const updatedItem =
        response.data as ReminderTemplateResponse['data'][number]
      queryClient.setQueriesData<ReminderTemplateResponse>(
        { queryKey: reminderTemplateKeys.all },
        (old) => {
          if (!old?.data) {
            return old
          }
          return {
            ...old,
            data: old.data.map((t) =>
              t.id === updatedItem.id ? updatedItem : t,
            ),
          }
        },
      )
      queryClient.setQueryData<ReminderTemplateByIdResponse>(
        reminderTemplateKeys.detail(provetId.value, reminderTemplateId),
        (old) => (old ? { ...old, data: response.data } : old),
      )
    },
  })
}

// ? reminder template DELETE to delete a specific one
export function useReminderTemplateDeleteMutation() {
  const api = useApi()
  const provetId = useRouteParams<string>('provetId')
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (payload: { id: string }) =>
      api((client) =>
        client.DELETE(REMINDER_TEMPLATE_BY_ID_URL, {
          params: {
            path: {
              provet_id: provetId.value,
              id: payload.id,
            },
          },
        }),
      ),
    onSuccess: (_, payload) => {
      queryClient.setQueriesData<ReminderTemplateResponse>(
        { queryKey: reminderTemplateKeys.all },
        (old) => {
          if (!old?.data) {
            return old
          }
          return {
            ...old,
            data: old.data.filter((t) => t.id !== payload.id),
          }
        },
      )
    },
  })
}
